/**
 * TanStack Query hooks, one group per resource. Mutations invalidate the work-related caches they affect
 * so the overview, period workplace and badges stay consistent without manual refreshes.
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import type {
  AccountClosureDTO,
  IcpDTO,
  KorDTO,
  CsvMappingDTO,
  AccountDeletionBody,
  OpsClosureDTO,
  ImportConfirmBody,
  PortalDTO,
  AccountantDTO,
  AccountantInput,
  AccountRefDTO,
  OpsReferralDTO,
  ReferralBody,
  ReferralOptionsDTO,
  ReferralResultDTO,
  ReferralStatus,
  AuditEventDTO,
  AuthorizationDTO,
  ConfirmBody,
  BankAccountDTO,
  BankTemplateDTO,
  BillingDTO,
  BillingCycle,
  ChannelStatus,
  ChecklistItemDTO,
  ClientRuleDTO,
  CorrectionDTO,
  CreateEntityBody,
  DocumentDTO,
  DocumentLineDTO,
  DocumentSummaryDTO,
  DraftDTO,
  DrillFactDTO,
  EntityDTO,
  FeaturesDTO,
  YearEndAnswers,
  GlobalRuleDTO,
  ImportPreviewDTO,
  IntakeAnswers,
  IntakeResult,
  KvkLookupDTO,
  LoginBody,
  MeDTO,
  MemberDTO,
  MissingDocGroupDTO,
  NotificationDTO,
  NotificationSettingsDTO,
  OpsAccountDTO,
  OpsKvkCheckDTO,
  KvkCheckStatus,
  OpsReviewOrderDTO,
  OpsTicketDTO,
  OpsTicketSummaryDTO,
  OverviewDTO,
  PeriodDetailDTO,
  PeriodListItemDTO,
  PlanCode,
  QualityDTO,
  QuestionDTO,
  RegisterBody,
  ReviewDTO,
  Role,
  SearchResultDTO,
  TransactionDTO,
  TxFilter,
  UnsurePath,
  UserDTO,
  BoxCode,
} from '@btw/shared';
import { api, del, get, patch, post, postForm, put } from './client';

/* ───────── keys ───────── */
export const qk = {
  me: ['me'] as const,
  overview: (quarter?: string) => ['overview', quarter ?? 'default'] as const,
  entities: ['entities'] as const,
  entity: (id: string) => ['entities', id] as const,
  periods: (f: object) => ['periods', f] as const,
  period: (entityId: string, quarter: string) => ['period', entityId, quarter] as const,
  draft: (entityId: string, quarter: string) => ['period', entityId, quarter, 'draft'] as const,
  drill: (entityId: string, quarter: string, box: string) => ['period', entityId, quarter, 'drill', box] as const,
  review: (entityId: string, quarter: string) => ['period', entityId, quarter, 'review'] as const,
  portal: (entityId: string, quarter: string) => ['period', entityId, quarter, 'portal'] as const,
  icp: (entityId: string, quarter: string) => ['period', entityId, quarter, 'icp'] as const,
  kor: (entityId: string) => ['kor', entityId] as const,
  corrections: (entityId?: string) => ['corrections', entityId ?? 'all'] as const,
  questions: (f: object) => ['questions', f] as const,
  transactions: (f: object) => ['transactions', f] as const,
  imports: (entityId?: string) => ['imports', entityId ?? 'all'] as const,
  documents: (f: object) => ['documents', f] as const,
  document: (id: string) => ['document', id] as const,
  rules: ['rules'] as const,
  ruleImpact: (id: string) => ['rules', id, 'impact'] as const,
  members: ['members'] as const,
  billing: ['billing'] as const,
  accountDeletion: ['account', 'deletion'] as const,
  notifications: ['notifications'] as const,
  notificationSettings: ['notification-settings'] as const,
  audit: (f: object) => ['audit', f] as const,
  search: (q: string) => ['search', q] as const,
  ops: (what: string) => ['ops', what] as const,
};

/** Anything that changes a period's facts can move every summary on screen. */
export function invalidateWork(qc: QueryClient): Promise<unknown> {
  return Promise.all(
    ['overview', 'periods', 'period', 'questions', 'transactions', 'documents', 'document', 'notifications', 'corrections', 'audit', 'kor'].map((k) =>
      qc.invalidateQueries({ queryKey: [k] }),
    ),
  );
}

/* ───────── auth & me ───────── */
export function useMe(enabled = true) {
  return useQuery({
    queryKey: qk.me,
    queryFn: () => api<MeDTO>('/auth/me', { silentAuth: true }),
    retry: false,
    staleTime: 60_000,
    // Prelaunch: /api/auth/me does not exist (404); never probe the session there.
    ...(enabled ? {} : { enabled: false }),
  });
}

function useSetMe() {
  const qc = useQueryClient();
  return (me: MeDTO) => {
    qc.setQueryData(qk.me, me);
  };
}

export function useLogin() {
  const setMe = useSetMe();
  return useMutation({
    mutationFn: (body: LoginBody) => api<MeDTO>('/auth/login', { method: 'POST', body, silentAuth: true }),
    onSuccess: setMe,
  });
}
export function useRegister() {
  const setMe = useSetMe();
  return useMutation({
    mutationFn: (body: RegisterBody) => api<MeDTO>('/auth/register', { method: 'POST', body, silentAuth: true }),
    onSuccess: setMe,
  });
}
/** P0-16: accept the current terms after a change (MeDTO.termsRequired). */
export function useAcceptTerms() {
  const setMe = useSetMe();
  return useMutation({ mutationFn: (termsVersion: string) => post<MeDTO>('/auth/accept-terms', { termsVersion }), onSuccess: setMe });
}
export function useMagicLink() {
  return useMutation({ mutationFn: (email: string) => post<{ ok: true }>('/auth/magic-link', { email }) });
}
/** Sign in with the token from a login link (/login/link#<token>). */
export function useSignInWithLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (token: string) => api<{ totpRequired: boolean }>('/auth/magic', { method: 'POST', body: { token }, silentAuth: true }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.me }),
  });
}
/** Confirm the email address with the token from /login/verify-email#<token>. */
export function useVerifyEmail() {
  return useMutation({ mutationFn: (token: string) => api<{ ok: true }>('/auth/verify-email', { method: 'POST', body: { token }, silentAuth: true }) });
}
export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => post<{ ok: true }>('/auth/logout'),
    onSettled: () => {
      qc.clear();
    },
  });
}
export function useSwitchAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (accountId: string) => post<MeDTO>('/auth/switch-account', { accountId }),
    onSuccess: (me) => {
      qc.clear();
      qc.setQueryData(qk.me, me);
    },
  });
}
/** Send the e-mail confirmation again (needed before two-step verification, review 2026-10-28). */
export function useResendVerification() {
  return useMutation({ mutationFn: () => post<{ ok: true; alreadyVerified: boolean }>('/auth/verify-email/resend') });
}

export function useTotpSetup() {
  return useMutation({ mutationFn: () => post<{ otpauthUrl: string; secret: string }>('/auth/totp/setup') });
}
export function useTotpEnable() {
  const setMe = useSetMe();
  return useMutation({ mutationFn: (code: string) => post<MeDTO>('/auth/totp/enable', { code }), onSuccess: setMe });
}
/** Recovery codes for a lost authenticator (review 2026-10-29). */
export function useRecoveryCodesLeft(enabled: boolean) {
  return useQuery({ queryKey: ['recovery-codes'], queryFn: () => get<{ left: number }>('/auth/totp/recovery-codes'), enabled });
}
export function useCreateRecoveryCodes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => post<{ codes: string[]; left: number }>('/auth/totp/recovery-codes'),
    onSuccess: (r) => qc.setQueryData(['recovery-codes'], { left: r.left }),
  });
}
export function reauth(body: { code?: string; password?: string }) {
  return api<{ reauthValidUntil: string }>('/auth/reauth', { method: 'POST', body, allowReauth: false });
}
export function usePatchMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<Pick<UserDTO, 'name' | 'locale' | 'theme' | 'largeText'>>) => patch<UserDTO>('/me', body),
    onMutate: async (body) => {
      // Optimistic: language/theme switches must feel instant (§9.1).
      const prev = qc.getQueryData<MeDTO>(qk.me);
      if (prev) qc.setQueryData<MeDTO>(qk.me, { ...prev, user: { ...prev.user, ...body } });
      return { prev };
    },
    onError: (_e, _b, ctx) => {
      if (ctx?.prev) qc.setQueryData(qk.me, ctx.prev);
    },
    onSuccess: (user) => {
      const prev = qc.getQueryData<MeDTO>(qk.me);
      if (prev) qc.setQueryData<MeDTO>(qk.me, { ...prev, user });
    },
  });
}

export function currentAccount(me: MeDTO | undefined): AccountRefDTO | null {
  if (!me) return null;
  return me.accounts.find((a) => a.id === me.currentAccountId) ?? me.accounts[0] ?? null;
}

/* ───────── overview ───────── */
export function useOverview(quarter?: string | null) {
  return useQuery({
    queryKey: qk.overview(quarter ?? undefined),
    queryFn: ({ signal }) => get<OverviewDTO>('/overview', { quarter: quarter ?? undefined }, signal),
    // P0-8: Home shows "Bezig met herberekenen…" and picks up the new amount by itself.
    refetchInterval: (q) => (q.state.data?.entities?.some((r) => r.recomputing) ? RECOMPUTE_POLL_MS : false),
  });
}

/* ───────── feature switches ───────── */
/** GET /api/features: e.g. the advisor review add-on (off until the partner accountant takes it over, TS-08). */
export function useFeatures() {
  return useQuery({ queryKey: ['features'], queryFn: ({ signal }) => get<FeaturesDTO>('/features', undefined, signal), staleTime: 5 * 60_000 });
}

/* ───────── entities ───────── */
export function useEntities(enabled = true) {
  return useQuery({ queryKey: qk.entities, queryFn: ({ signal }) => get<EntityDTO[]>('/entities', undefined, signal), enabled });
}
export function useEntity(id: string | undefined) {
  return useQuery({
    queryKey: qk.entity(id ?? ''),
    queryFn: ({ signal }) => get<EntityDTO>(`/entities/${id}`, undefined, signal),
    enabled: !!id,
  });
}
function useEntityMutation<TVars, TRes>(fn: (v: TVars) => Promise<TRes>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.entities });
      invalidateWork(qc);
    },
  });
}
export const useCreateEntity = () => useEntityMutation((body: CreateEntityBody) => post<EntityDTO>('/entities', body));
export const usePatchEntity = (id: string) =>
  useEntityMutation((body: Partial<CreateEntityBody & { channelStatus: ChannelStatus }>) => patch<EntityDTO>(`/entities/${id}`, body));
export const useIntake = () =>
  useEntityMutation(({ id, answers }: { id: string; answers: IntakeAnswers }) =>
    post<{ entity: EntityDTO; result: IntakeResult }>(`/entities/${id}/intake`, answers),
  );
export const useAddBankAccount = () =>
  useEntityMutation(({ entityId, ...body }: { entityId: string; iban: string; bank?: string; label?: string; isPrivate?: boolean }) =>
    post<BankAccountDTO>(`/entities/${entityId}/bank-accounts`, body),
  );
export const useRemoveBankAccount = () =>
  useEntityMutation(({ entityId, bankAccountId }: { entityId: string; bankAccountId: string }) =>
    del<{ ok: true }>(`/entities/${entityId}/bank-accounts/${bankAccountId}`),
  );
export const usePreviousReturn = () =>
  useEntityMutation(
    ({ entityId, ...body }: { entityId: string; quarter: string; balanceEuro: number; receiptNumber?: string; hadCorrections: boolean }) =>
      post<{ ok: true }>(`/entities/${entityId}/previous-return`, body),
  );
export function kvkLookup(kvk: string, signal?: AbortSignal) {
  return get<KvkLookupDTO>(`/kvk/${encodeURIComponent(kvk)}`, undefined, signal);
}

/* ───────── periods ───────── */
/** While a return is being recomputed (P0-8) the lists poll until the new figures are there. */
const RECOMPUTE_POLL_MS = 3000;

export function usePeriods(f: { quarter?: string; entityId?: string; status?: string }) {
  return useQuery({
    queryKey: qk.periods(f),
    queryFn: ({ signal }) => get<PeriodListItemDTO[]>('/periods', f, signal),
    refetchInterval: (q) => (q.state.data?.some((r) => r.recomputing) ? RECOMPUTE_POLL_MS : false),
  });
}
const periodPath = (e: string, q: string) => `/periods/${e}/${q}`;
export function usePeriod(entityId: string, quarter: string) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: qk.period(entityId, quarter),
    queryFn: async ({ signal }) => {
      const before = qc.getQueryData<PeriodDetailDTO>(qk.period(entityId, quarter));
      const next = await get<PeriodDetailDTO>(periodPath(entityId, quarter), undefined, signal);
      // P0-8: the recompute just finished — the draft, portal values and lists changed with it.
      if (before?.recomputing && !next.recomputing) {
        for (const key of [qk.draft(entityId, quarter), qk.portal(entityId, quarter), ['overview'], ['periods']]) void qc.invalidateQueries({ queryKey: key });
      }
      return next;
    },
    enabled: !!entityId && !!quarter,
    // Keep the page current while the review runs by itself (2026-10) or the figures are being recomputed (P0-8).
    refetchInterval: (q) => (q.state.data?.recomputing ? RECOMPUTE_POLL_MS : q.state.data?.status === 'in_review' ? 4000 : false),
  });
}
export function useDraft(entityId: string, quarter: string, enabled = true) {
  return useQuery({
    queryKey: qk.draft(entityId, quarter),
    queryFn: ({ signal }) => get<DraftDTO>(`${periodPath(entityId, quarter)}/draft`, undefined, signal),
    enabled,
    retry: (n, e) => n < 1 && !(e as { status?: number }).status,
  });
}
export function useDrill(entityId: string, quarter: string, box: BoxCode | null) {
  return useQuery({
    queryKey: qk.drill(entityId, quarter, box ?? ''),
    queryFn: ({ signal }) => get<DrillFactDTO[]>(`${periodPath(entityId, quarter)}/draft/drill`, { box: box ?? undefined }, signal),
    enabled: !!box,
  });
}
export function useReview(entityId: string, quarter: string, enabled = true) {
  return useQuery({
    queryKey: qk.review(entityId, quarter),
    queryFn: ({ signal }) => get<ReviewDTO>(`${periodPath(entityId, quarter)}/review`, undefined, signal),
    enabled,
    refetchInterval: (q) => (q.state.data?.state === 'running' ? 4000 : false),
  });
}
export function usePortal(entityId: string, quarter: string, enabled = true) {
  return useQuery({
    queryKey: qk.portal(entityId, quarter),
    queryFn: ({ signal }) =>
      get<PortalDTO>(`${periodPath(entityId, quarter)}/portal`, undefined, signal),
    enabled,
  });
}
/** EC sales list lines (ICP-opgaaf), only fetched when the draft says one is needed. */
export function useIcp(entityId: string, quarter: string, enabled = true) {
  return useQuery({
    queryKey: qk.icp(entityId, quarter),
    queryFn: ({ signal }) => get<IcpDTO>(`${periodPath(entityId, quarter)}/icp`, undefined, signal),
    enabled,
  });
}
/** KOR company (2026-10-30): this year's and last year's turnover against the € 20,000 limit. */
export function useKor(entityId: string | undefined) {
  return useQuery({
    queryKey: qk.kor(entityId ?? ''),
    queryFn: ({ signal }) => get<KorDTO>(`/entities/${entityId}/kor`, undefined, signal),
    enabled: !!entityId,
  });
}

/** Shared with the API (api.ts); `recomputing` withholds the values of a draft that is about to change (AR-08). */
export type { PortalDTO };

function usePeriodMutation<TVars, TRes>(entityId: string, quarter: string, fn: (v: TVars) => Promise<TRes>, opts?: { setPeriod?: boolean }) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (res) => {
      if (opts?.setPeriod && res && typeof res === 'object' && 'steps' in (res as object)) {
        qc.setQueryData(qk.period(entityId, quarter), res);
      }
      invalidateWork(qc);
    },
  });
}
export const useRecompute = (e: string, q: string) =>
  usePeriodMutation(e, q, (_: void) => post<PeriodDetailDTO>(`${periodPath(e, q)}/recompute`), { setPeriod: true });
export const useStartReview = (e: string, q: string) => usePeriodMutation(e, q, (_: void) => post<ReviewDTO>(`${periodPath(e, q)}/review/start`));
export const useResolveFinding = (e: string, q: string) =>
  usePeriodMutation(e, q, ({ id, choice }: { id: string; choice: 'draft' | 'review' }) =>
    post<ReviewDTO>(`${periodPath(e, q)}/review/findings/${id}/resolve`, { choice }),
  );
/** Q4 year-end check (TS-06): three yes/no answers; any 'yes' puts the return out of scope. */
export const useYearEnd = (e: string, q: string) =>
  usePeriodMutation(e, q, (answers: YearEndAnswers) => post<PeriodDetailDTO>(`${periodPath(e, q)}/year-end`, answers), { setPeriod: true });
/** Confirm the figures = authorize (2026-10): one action after one two-step code, bound to the draft the user saw. */
export const useConfirmReturn = (e: string, q: string) =>
  usePeriodMutation(e, q, (body: ConfirmBody) => post<AuthorizationDTO>(`${periodPath(e, q)}/confirm`, body));
export function usePortalCheck(e: string, q: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { code: BoxCode; checked: boolean }) =>
      post<PortalDTO>(`${periodPath(e, q)}/portal/check`, v),
    onMutate: async (v) => {
      const key = qk.portal(e, q);
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<PortalDTO>(key);
      if (prev) {
        const rows = prev.rows.map((r) => (r.code === v.code ? { ...r, checked: v.checked } : r));
        qc.setQueryData(key, { ...prev, rows, checkedCount: rows.filter((r) => r.checked).length, checkedRequiredCount: rows.filter((r) => r.required && r.checked).length });
      }
      return { prev };
    },
    onError: (_err, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(qk.portal(e, q), ctx.prev);
    },
    onSuccess: (data) => qc.setQueryData(qk.portal(e, q), data),
  });
}
export const usePortalMismatch = (e: string, q: string) =>
  usePeriodMutation(e, q, (v: { code: BoxCode; portalValueEuro: number; note?: string }) =>
    post<{ ticketId: string }>(`${periodPath(e, q)}/portal/mismatch`, v),
  );
export const useFiled = (e: string, q: string) => usePeriodMutation(e, q, (_: void) => post<PeriodDetailDTO>(`${periodPath(e, q)}/filed`), { setPeriod: true });
/** DC-05: "Toch nog niet ingediend" within 24 h of "Ik heb ingediend". */
export const useUndoFiled = (e: string, q: string) => usePeriodMutation(e, q, (_: void) => post<PeriodDetailDTO>(`${periodPath(e, q)}/filed/undo`), { setPeriod: true });
/** "Al elders ingediend / niet van toepassing" — off the work list; undone from History. */
export const useDismissPeriod = (e: string, q: string) => usePeriodMutation(e, q, (_: void) => post<PeriodDetailDTO>(`${periodPath(e, q)}/dismiss`), { setPeriod: true });
export const useUndoDismissPeriod = (e: string, q: string) => usePeriodMutation(e, q, (_: void) => post<PeriodDetailDTO>(`${periodPath(e, q)}/dismiss/undo`), { setPeriod: true });
export const useReceipt = (e: string, q: string) =>
  usePeriodMutation(
    e,
    q,
    (v: { file: File; receiptNumber: string; receivedOn: string }) => {
      const fd = new FormData();
      fd.append('file', v.file);
      fd.append('receiptNumber', v.receiptNumber);
      fd.append('receivedOn', v.receivedOn);
      return postForm<PeriodDetailDTO>(`${periodPath(e, q)}/receipt`, fd);
    },
    { setPeriod: true },
  );
export const useAdvisorReview = (e: string, q: string) =>
  usePeriodMutation(e, q, (_: void) => post<PeriodDetailDTO>(`${periodPath(e, q)}/advisor-review`), { setPeriod: true });
/** Partner accountant referral with explicit consent (P0-14). */
export const useReferral = (e: string, q: string) =>
  usePeriodMutation(e, q, (body: ReferralBody) => post<ReferralResultDTO>(`${periodPath(e, q)}/referral`, body));
/** Whether a partner firm is active (only then the 2-working-day promise), consent version and shareable questions. */
export const useReferralOptions = (entityId: string | null, quarter: string, enabled = true) =>
  useQuery({
    queryKey: ['referral-options', entityId, quarter],
    queryFn: ({ signal }) => get<ReferralOptionsDTO>('/referrals/options', entityId ? { entityId, quarter } : {}, signal),
    enabled,
    staleTime: 30_000,
  });
export function useCorrections(entityId?: string) {
  return useQuery({ queryKey: qk.corrections(entityId), queryFn: ({ signal }) => get<CorrectionDTO[]>('/corrections', { entityId }, signal) });
}
export function useMarkCorrectionFiled() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => post<CorrectionDTO>(`/corrections/${id}/mark-filed`), onSuccess: () => invalidateWork(qc) });
}
export const periodFileUrl = (e: string, q: string, file: 'archive.zip' | 'workpaper.csv') => `/api/periods/${e}/${q}/${file}`;

/* ───────── questions ───────── */
export function useQuestions(f: { status?: string; entityId?: string; quarter?: string }, enabled = true) {
  return useQuery({ queryKey: qk.questions(f), queryFn: ({ signal }) => get<QuestionDTO[]>('/questions', f, signal), enabled });
}
export function useAnswerQuestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; optionId: string; applyRule?: boolean; partialPct?: number; eurTotalCents?: number; amountCents?: number }) =>
      post<{ question: QuestionDTO; undoUntil: string; proposeRule: boolean }>(`/questions/${id}/answer`, body),
    onSuccess: () => invalidateWork(qc),
  });
}
export function useUndoQuestion() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => post<QuestionDTO>(`/questions/${id}/undo`), onSuccess: () => invalidateWork(qc) });
}
export function useUnsureQuestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, path }: { id: string; path: UnsurePath }) => post<QuestionDTO>(`/questions/${id}/unsure`, { path }),
    onSuccess: () => invalidateWork(qc),
  });
}
export function lockQuestion(id: string) {
  return post<{ ok: true }>(`/questions/${id}/lock`).catch(() => undefined);
}
export const accountantListUrl = (entityId?: string, quarter?: string) =>
  `/api/questions/accountant-list.csv?${new URLSearchParams({ ...(entityId ? { entityId } : {}), ...(quarter ? { quarter } : {}) })}`;

/* ───────── transactions & imports ───────── */
export function useTransactions(f: { entityId?: string; quarter?: string; filter?: TxFilter; q?: string; page?: number }) {
  return useQuery({
    queryKey: qk.transactions(f),
    queryFn: ({ signal }) => get<{ items: TransactionDTO[]; total: number }>('/transactions', f, signal),
    placeholderData: (prev) => prev,
  });
}
/** 资料: "没有发票" on a business payment (no deduction either way; takes it off the missing list). Undo with false. */
export function useNoInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, noInvoice }: { id: string; noInvoice: boolean }) => post<TransactionDTO>(`/transactions/${id}/no-invoice`, { noInvoice }),
    onSuccess: () => invalidateWork(qc),
  });
}
export function useImports(entityId?: string) {
  return useQuery({ queryKey: qk.imports(entityId), queryFn: ({ signal }) => get<ImportPreviewDTO[]>('/imports', { entityId }, signal) });
}
export function useUploadImport() {
  return useMutation({
    mutationFn: ({ file, entityId, bankAccountId }: { file: File; entityId: string; bankAccountId?: string }) => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('entityId', entityId);
      if (bankAccountId) fd.append('bankAccountId', bankAccountId);
      return postForm<ImportPreviewDTO>('/imports', fd);
    },
  });
}
/** A CSV column mapping; dateFormat/decimal 'auto' = detected from the data (the default). */
export type MappingBody = CsvMappingDTO;
export function useImportMapping() {
  return useMutation({ mutationFn: ({ id, ...body }: MappingBody & { id: string }) => post<ImportPreviewDTO>(`/imports/${id}/mapping`, body) });
}
export function useConfirmImport() {
  const qc = useQueryClient();
  return useMutation({
    // ZL-03: files without statement dates send the period the user confirmed.
    mutationFn: (v: string | ({ id: string } & ImportConfirmBody)) =>
      typeof v === 'string' ? post<ImportPreviewDTO>(`/imports/${v}/confirm`) : post<ImportPreviewDTO>(`/imports/${v.id}/confirm`, { periodStart: v.periodStart, periodEnd: v.periodEnd }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['imports'] });
      qc.invalidateQueries({ queryKey: qk.entities });
      invalidateWork(qc);
    },
  });
}

/* ───────── documents ───────── */
export function useDocuments(f: { entityId?: string; quarter?: string; status?: string; match?: string }) {
  return useQuery({ queryKey: qk.documents(f), queryFn: ({ signal }) => get<DocumentSummaryDTO[]>('/documents', f, signal) });
}
export function useDocument(id: string | null) {
  return useQuery({ queryKey: qk.document(id ?? ''), queryFn: ({ signal }) => get<DocumentDTO>(`/documents/${id}`, undefined, signal), enabled: !!id });
}
export function useUploadDocuments() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ files, entityId, source }: { files: File[]; entityId?: string; source?: 'upload' | 'photo' }) => {
      const fd = new FormData();
      // Fields before the files: the server reads them as the multipart stream arrives.
      if (entityId) fd.append('entityId', entityId);
      if (source) fd.append('source', source);
      files.forEach((f) => fd.append('files', f));
      return postForm<DocumentSummaryDTO[]>('/documents', fd);
    },
    onSuccess: () => invalidateWork(qc),
  });
}
export interface DocumentPatch {
  entityId?: string;
  fields?: {
    supplierName?: string;
    invoiceNumber?: string;
    invoiceDate?: string;
    direction?: 'purchase' | 'sales' | 'unknown';
    lines?: DocumentLineDTO[];
    reverseCharge?: boolean;
    supplierVatId?: string;
    supplierCountry?: string;
  };
}
export function usePatchDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: DocumentPatch & { id: string }) => patch<DocumentDTO>(`/documents/${id}`, body),
    onSuccess: (doc) => {
      qc.setQueryData(qk.document(doc.id), doc);
      invalidateWork(qc);
    },
  });
}
export function useDeleteDocument() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => del<{ ok: true }>(`/documents/${id}`), onSuccess: () => invalidateWork(qc) });
}
/** Indirection so the dev-only demo mode can serve generated files (img/object tags bypass fetch). */
export const fileUrls = { document: (id: string) => `/api/documents/${id}/file` };
export const documentFileUrl = (id: string) => fileUrls.document(id);

/* ───────── client rules ───────── */
export function useRules() {
  return useQuery({ queryKey: qk.rules, queryFn: ({ signal }) => get<ClientRuleDTO[]>('/rules', undefined, signal) });
}
export function useRuleImpact(id: string | null) {
  return useQuery({
    queryKey: qk.ruleImpact(id ?? ''),
    queryFn: ({ signal }) => get<{ transactions: number; quarters: string[]; lockedQuarters: string[] }>(`/rules/${id}/impact`, undefined, signal),
    enabled: !!id,
  });
}
export function useRevokeRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => del<{ ok: true }>(`/rules/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.rules });
      invalidateWork(qc);
    },
  });
}

/* ───────── members & invites ───────── */
export function useMembers(enabled = true) {
  return useQuery({ queryKey: qk.members, queryFn: ({ signal }) => get<MemberDTO[]>('/members', undefined, signal), enabled });
}
export function useInviteMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; role: Role; entityIds?: string[] }) => post<MemberDTO>('/members/invite', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.members }),
  });
}
export function useRemoveMember() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => del<{ ok: true }>(`/members/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: qk.members }) });
}
/** Invitation tokens are posted in the body (AR-05): never part of an API URL. */
export function useInviteInfo(token: string) {
  return useQuery({
    queryKey: ['invite', token],
    queryFn: () => api<{ accountName: string; email: string }>('/invites/describe', { method: 'POST', body: { token }, silentAuth: true }),
    enabled: token.length > 0,
    retry: false,
  });
}
export function useAcceptInvite(token: string) {
  const setMe = useSetMe();
  return useMutation({
    mutationFn: (body: { name?: string; password?: string; acceptTerms?: true; termsVersion?: string }) => api<MeDTO>('/invites/accept', { method: 'POST', body: { token, ...body }, silentAuth: true }),
    onSuccess: setMe,
  });
}

/* ───────── billing ───────── */
export function useBilling(enabled = true) {
  return useQuery({ queryKey: qk.billing, queryFn: ({ signal }) => get<BillingDTO>('/billing', undefined, signal), enabled });
}
export function useCheckout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { plan: Exclude<PlanCode, 'free_check'>; cycle: BillingCycle; payerEntityId: string }) => post<{ checkoutUrl: string | null; billing: BillingDTO }>('/billing/checkout', body),
    onSuccess: (r) => {
      qc.setQueryData(qk.billing, r.billing);
      qc.invalidateQueries({ queryKey: qk.me });
    },
  });
}
export function useCancelBilling() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => post<BillingDTO>('/billing/cancel'),
    onSuccess: (b) => {
      qc.setQueryData(qk.billing, b);
      qc.invalidateQueries({ queryKey: qk.me });
    },
  });
}
/* Account closure (retention policy): "Account verwijderen" in Profiel & beveiliging. */
export function useAccountDeletion(enabled = true) {
  return useQuery({ queryKey: qk.accountDeletion, queryFn: ({ signal }) => get<{ closure: AccountClosureDTO | null }>('/account/deletion', undefined, signal), enabled });
}
export function useRequestDeletion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AccountDeletionBody) => post<{ closure: AccountClosureDTO }>('/account/deletion', body),
    onSuccess: (r) => {
      qc.setQueryData(qk.accountDeletion, r);
      // The plan ended (read-only): session, billing and banners change.
      qc.invalidateQueries({ queryKey: qk.me });
      qc.invalidateQueries({ queryKey: qk.billing });
    },
  });
}
export function useWithdrawDeletion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => post<{ closure: null }>('/account/deletion/withdraw'),
    onSuccess: (r) => qc.setQueryData(qk.accountDeletion, r),
  });
}
export function useFreeCheck() {
  return useMutation({
    mutationFn: (entityId: string) => post<{ gaps: ChecklistItemDTO[]; missingDocs: MissingDocGroupDTO[]; transactions: number }>('/free-check', { entityId }),
  });
}

/* ───────── notifications ───────── */
export function useNotifications(enabled = true) {
  return useQuery({
    queryKey: qk.notifications,
    queryFn: ({ signal }) => get<{ items: NotificationDTO[]; unreadActionCount: number }>('/notifications', undefined, signal),
    enabled,
    refetchInterval: 60_000,
  });
}
export function useMarkNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[] | 'all') => post<{ ok: true }>('/notifications/read', { ids }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.notifications }),
  });
}
export function useNotificationSettings() {
  return useQuery({ queryKey: qk.notificationSettings, queryFn: ({ signal }) => get<NotificationSettingsDTO>('/notification-settings', undefined, signal) });
}
export function usePutNotificationSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: NotificationSettingsDTO) => put<NotificationSettingsDTO>('/notification-settings', body),
    onMutate: (body) => {
      const prev = qc.getQueryData<NotificationSettingsDTO>(qk.notificationSettings);
      qc.setQueryData(qk.notificationSettings, body);
      return { prev };
    },
    onError: (_e, _b, ctx) => {
      if (ctx?.prev) qc.setQueryData(qk.notificationSettings, ctx.prev);
    },
  });
}
export const getVapidKey = () => get<{ publicKey: string | null }>('/push/vapid-key');
export const subscribePush = (sub: PushSubscriptionJSON) => post<{ ok: true }>('/push/subscribe', sub);

/* ───────── audit ───────── */
export function useAudit(f: { entityId?: string; actor?: string; action?: string }) {
  return useInfiniteQuery({
    queryKey: qk.audit(f),
    queryFn: ({ pageParam, signal }) =>
      get<{ items: AuditEventDTO[]; nextCursor: string | null; chainOk: boolean }>('/audit', { ...f, cursor: pageParam ?? undefined }, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
}

/* ───────── search & support ───────── */
export function useSearch(q: string) {
  return useQuery({
    queryKey: qk.search(q),
    queryFn: ({ signal }) => get<SearchResultDTO[]>('/search', { q }, signal),
    enabled: q.trim().length >= 2,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}
export function useCreateTicket() {
  return useMutation({
    mutationFn: (body: { subject: string; body: string; entityId?: string; quarter?: string }) => post<{ id: string }>('/support/tickets', body),
  });
}

/* ───────── ops back office ───────── */
export const useOpsAccounts = () => useQuery({ queryKey: qk.ops('accounts'), queryFn: () => get<OpsAccountDTO[]>('/ops/accounts') });
export const useOpsReviewQueue = () => useQuery({ queryKey: qk.ops('review'), queryFn: () => get<OpsReviewOrderDTO[]>('/ops/review-queue') });
export const useOpsTickets = () => useQuery({ queryKey: qk.ops('tickets'), queryFn: () => get<OpsTicketSummaryDTO[]>('/ops/tickets') });
export const useOpsTicket = (id: string | null) =>
  useQuery({ queryKey: [...qk.ops('tickets'), id], queryFn: () => get<OpsTicketDTO>(`/ops/tickets/${id}`), enabled: !!id });
export const useOpsGlobalRules = () => useQuery({ queryKey: qk.ops('rules'), queryFn: () => get<GlobalRuleDTO[]>('/ops/global-rules') });
export const useOpsBankTemplates = () => useQuery({ queryKey: qk.ops('templates'), queryFn: () => get<BankTemplateDTO[]>('/ops/bank-templates') });
export interface OpsNotificationRow {
  id: string;
  accountName: string;
  kind: string;
  channel: string;
  sentAt: string;
  status: string;
}
export const useOpsClosures = () => useQuery({ queryKey: qk.ops('closures'), queryFn: () => get<OpsClosureDTO[]>('/ops/closures') });
export const useOpsNotifications = () =>
  useQuery({ queryKey: qk.ops('notifications'), queryFn: () => get<OpsNotificationRow[]>('/ops/notifications', { limit: 100 }) });
export const useOpsQuality = (quarter?: string) =>
  useQuery({ queryKey: qk.ops(`quality-${quarter ?? ''}`), queryFn: () => get<QualityDTO>('/ops/quality', { quarter }) });

function useOpsMutation<TVars, TRes>(what: string, fn: (v: TVars) => Promise<TRes>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => qc.invalidateQueries({ queryKey: qk.ops(what) }) });
}
export const useOpsKvkChecks = () => useQuery({ queryKey: qk.ops('kvk'), queryFn: () => get<OpsKvkCheckDTO[]>('/ops/kvk-checks') });
export const useOpsSetKvkCheck = () =>
  useOpsMutation('kvk', ({ id, status, note }: { id: string; status: KvkCheckStatus; note?: string | null }) =>
    post<OpsKvkCheckDTO>(`/ops/kvk-checks/${id}`, { status, note: note ?? null }),
  );
export const useOpsAccountants = () => useQuery({ queryKey: qk.ops('accountants'), queryFn: () => get<AccountantDTO[]>('/ops/accountants') });
export const useOpsReferrals = () => useQuery({ queryKey: qk.ops('referrals'), queryFn: () => get<OpsReferralDTO[]>('/ops/referrals') });
export const useOpsSaveAccountant = () =>
  useOpsMutation('accountants', ({ id, ...body }: Partial<AccountantInput> & { id?: string }) =>
    id ? patch<AccountantDTO>(`/ops/accountants/${id}`, body) : post<AccountantDTO>('/ops/accountants', body),
  );
export const useOpsUpdateReferral = () =>
  useOpsMutation('referrals', ({ id, ...body }: { id: string; status?: ReferralStatus; ownerId?: string | null; accountantId?: string; notes?: string }) =>
    patch<OpsReferralDTO>(`/ops/referrals/${id}`, body),
  );
export const useOpsRecordFee = () =>
  useOpsMutation('referrals', ({ id, firstYearFeeCents }: { id: string; firstYearFeeCents: number }) => post<OpsReferralDTO>(`/ops/referrals/${id}/fee`, { firstYearFeeCents }));
export const useOpsClaim = () => useOpsMutation('review', (id: string) => post<OpsReviewOrderDTO>(`/ops/review-queue/${id}/claim`));
export const useOpsComplete = () =>
  useOpsMutation('review', ({ id, notes }: { id: string; notes: string }) => post<OpsReviewOrderDTO>(`/ops/review-queue/${id}/complete`, { notes }));
export const useOpsReply = () =>
  useOpsMutation('tickets', ({ id, body, close }: { id: string; body: string; close?: boolean }) => post<OpsTicketDTO>(`/ops/tickets/${id}/reply`, { body, close }));
export const useOpsCreateRule = () => useOpsMutation('rules', (body: Omit<GlobalRuleDTO, 'id'>) => post<GlobalRuleDTO>('/ops/global-rules', body));
export const useOpsPatchRule = () =>
  useOpsMutation('rules', ({ id, ...body }: Partial<GlobalRuleDTO> & { id: string }) => patch<GlobalRuleDTO>(`/ops/global-rules/${id}`, body));
